import { useEffect, useState } from "react";
import { styled, darkTheme } from "~/ui/style/stitches.config";
import { ChevronDownIcon } from "@radix-ui/react-icons";

interface FilterOption {
  value: string;
  label: string;
}

interface JournalFiltersProps {
  onMonthFilter: (month: string | null) => void;
  onSearchFilter: (search: string | null) => void;
  entries: any[];
}

export const JournalFilters = ({ onMonthFilter, onSearchFilter, entries }: JournalFiltersProps) => {
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);

  // Generate all 12 months in reverse chronological order from current month
  const getMonthOptions = (): FilterOption[] => {
    const months = [
      'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
      'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'
    ];
    
    const currentMonth = new Date().getMonth(); // 0-11
    const reorderedMonths = [];
    
    // Start from current month and go backwards
    for (let i = 0; i < 12; i++) {
      const monthIndex = (currentMonth - i + 12) % 12;
      reorderedMonths.push(months[monthIndex]);
    }
    
    return reorderedMonths.map((month) => {
      const index = months.indexOf(month);
      return {
        value: String(index + 1).padStart(2, '0'),
        label: month
      };
    });
  };

  const monthOptions = getMonthOptions();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.month-dropdown-container')) {
        setIsMonthDropdownOpen(false);
      }
    };

    if (isMonthDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMonthDropdownOpen]);

  const handleMonthSelect = (month: string | null) => {
    setSelectedMonth(month);
    setIsMonthDropdownOpen(false);
    onMonthFilter(month);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    onSearchFilter(value.trim() || null);
  };

  const clearSearch = () => {
    setSearchTerm("");
    onSearchFilter(null);
  };

  return (
    <StyledJournalFilters>
      <div className="filters-container">
        {/* Month Dropdown */}
        <div className="month-dropdown-container">
          <div 
            className={`dropdown-button ${isMonthDropdownOpen ? 'open' : ''}`}
            onClick={() => setIsMonthDropdownOpen(!isMonthDropdownOpen)}
          >
            <span>{selectedMonth ? monthOptions.find(m => m.value === selectedMonth)?.label : 'Month'}</span>
            <ChevronDownIcon
              className="dropdown-arrow"
              style={{
                transform: isMonthDropdownOpen ? "rotate(-180deg)" : "rotate(0deg)",
                transition: "all 0.2s ease-in-out",
              }}
            />
          </div>
          
          {isMonthDropdownOpen && (
            <div className="dropdown-menu">
              <div 
                className={`dropdown-item ${selectedMonth === null ? 'selected' : ''}`}
                onClick={() => handleMonthSelect(null)}
              >
                All
                <img
                  className="checkmark"
                  src="/images/more-menu/checkmark.svg"
                  alt="selected"
                />
              </div>
              {monthOptions.map(option => (
                <div
                  key={option.value}
                  className={`dropdown-item ${selectedMonth === option.value ? 'selected' : ''}`}
                  onClick={() => handleMonthSelect(option.value)}
                >
                  {option.label}
                  <img
                    className="checkmark"
                    src="/images/more-menu/checkmark.svg"
                    alt="selected"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Search Bar */}
        <div className="search-container">
          <img src="/images/search-glass.svg" alt="search" className="search-icon" />
          <input
            type="text"
            placeholder="Card / keywords"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="search-input"
          />
          <div
            className={`clear-icon visible-${searchTerm !== null && searchTerm !== ""}`}
            onClick={clearSearch}
          />
        </div>
      </div>
    </StyledJournalFilters>
  );
};

const StyledJournalFilters = styled("div", {
  width: "100%",
  marginBottom: "30px",
  display: "flex",
  justifyContent: "center",
  "& .filters-container": {
    display: "flex",
    gap: "10px",
    alignItems: "center",
    
    "@mobile": {
      flexDirection: "row",
      gap: "10px",
    },
  },
  
  "& .month-dropdown-container": {
    position: "relative",
    minWidth: "120px",
    
    "@mobile": {
      minWidth: "100px",
      flex: "0 0 auto",
    },
    
    "& .dropdown-button": {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "8px 12px 8px 6px",
      border: "0.5px solid rgba(0, 0, 0, 0.4)",
      borderRadius: "48px",
      backgroundColor: "#fff",
      cursor: "pointer",
      fontSize: "11px",
      textTransform: "uppercase",
      transition: "border-color 0.2s ease",
      height: "36px",
      boxSizing: "border-box",
      position: "relative",
      
      "&:hover, &.open": {
        borderColor: "#000",
      },
      
      "& .dropdown-arrow": {
        position: "absolute",
        right: "12px",
        width: "16px",
        height: "16px",
      },
    },
    
    "& .dropdown-menu": {
      position: "absolute",
      top: "100%",
      left: "0",
      right: "0",
      marginTop: "4px",
      backgroundColor: "#fff",
      border: "0.5px solid black",
      borderRadius: "18px",
      padding: "16px 8px 16px 8px",
      zIndex: 10,
      maxHeight: "200px",
      overflowY: "auto",
      
      "&::-webkit-scrollbar": {
        width: "5px",
      },
      "&::-webkit-scrollbar-thumb": {
        borderRadius: "6px",
        backgroundColor: "rgba(127, 127, 127, .5)",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "transparent",
        marginTop: "16px",
        marginBottom: "16px",
      },
      "&::-webkit-scrollbar-corner": {
        backgroundColor: "transparent",
      },
      
      "& .dropdown-item": {
        padding: "10px 8px",
        fontSize: "11px",
        textTransform: "uppercase",
        cursor: "pointer",
        borderRadius: "7px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        transition: "background-color 0.2s ease",
        marginTop: "2px",
        
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.05)",
        },
        
        "& .checkmark": {
          opacity: 0,
          transition: "opacity 0.2s ease",
        },
        
        "&.selected .checkmark": {
          opacity: 1,
        },
      },
    },
  },
  
  "& .search-container": {
    position: "relative",
    flex: 1,
    maxWidth: "300px",
    
    "@mobile": {
      width: "100%",
      maxWidth: "none",
    },
    
    "& .search-icon": {
      position: "absolute",
      left: "8px",
      top: "calc(50% - 10px)",
      width: "20px",
      height: "20px",
      pointerEvents: "none",
      opacity: 0.7,
    },
    
    "& .search-input": {
      width: "100%",
      height: "36px",
      padding: "0px 16px",
      paddingLeft: "30px",
      paddingRight: "26px",
      border: "0.5px solid rgba(0, 0, 0, 0.4)",
      borderRadius: "48px",
      fontSize: "12px",
      outline: "none",
      transition: "border-color 0.2s ease",
      
      "&::placeholder": {
        color: "rgba(0, 0, 0, 0.4)",
      },
      
      "&:focus": {
        borderColor: "#000",
      },
    },
    
    "& .clear-icon": {
      position: "absolute",
      right: "5px",
      top: "calc(50% - 12.5px)",
      width: "25px",
      height: "25px",
      cursor: "pointer",
      filter: "invert(0.5)",
      pointerEvents: "auto",
      transition: "filter 0.2s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      
      "&::before": {
        content: '""',
        position: "absolute",
        width: "15px",
        height: "15px",
        backgroundImage: "url(/images/room/x-icon.png)",
        backgroundSize: "contain",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        filter: "inherit",
      },
      
      "&.visible-false": {
        display: "none",
      },
      
      "&:hover": {
        filter: "invert(0.3)",
      },
    },
  },

  [`.${darkTheme.className} &`]: {
    "& .month-dropdown-container .dropdown-button, & .month-dropdown-container .dropdown-menu": { backgroundColor: "$surface", color: "$text", borderColor: "rgba(255, 255, 255, 0.4)" },
    "& .month-dropdown-container .dropdown-button:hover, & .month-dropdown-container .dropdown-button.open": { borderColor: "#fff" },
    "& .month-dropdown-container .dropdown-menu .dropdown-item:hover": { backgroundColor: "rgba(255, 255, 255, 0.12)" },
    "& .dropdown-arrow, & .search-icon": { filter: "invert(1)" },
    "& .search-container .search-input": { backgroundColor: "$surface", color: "$text", borderColor: "rgba(255, 255, 255, 0.4)", "&::placeholder": { color: "rgba(255, 255, 255, 0.4)" }, "&:focus": { borderColor: "#fff" } },
  },
});
